/**
 * useDealScreenStatus — Polling hook for Deal Screen pipeline status
 * Authority: backend/config/deal_screen_pipeline_status_config.json
 *
 * Polls /api/deal-screen/status/{startup_id} every 4 seconds until a terminal
 * state is reached. Terminal states: complete | failed | partial.
 *
 * NOT terminal: not_started — the pipeline background task may not have written
 * its first status yet. Keep polling.
 *
 * CLIENT-SIDE ESCAPE HATCH (WI-DS-STUCK-RUN-RCA-RETRY-001)
 * A run whose background task was dropped (Render free-tier dyno suspended
 * before/while running) never writes a terminal status, so this poll would spin
 * forever and the Analyze/Re-run button would stay locked with no error and no
 * retry — the exact lock the CEO hit. The hook now bounds the wait and, on
 * deadline, SYNTHESISES a terminal `failed` (error_key='frontend_timeout') so
 * the UI can show a plain-English error + Retry. Two deadlines, both chosen to
 * NEVER false-fail a healthy run:
 *   - NEVER_STARTED: the run never advanced past not_started/no-status within
 *     NEVER_STARTED_DEADLINE_MS. A healthy /run writes `running` within ~30s of
 *     submit, so a run still stuck at not_started after 2.5 min is dead.
 *   - HARD_CEILING: an absolute ceiling (7 min) far above the observed healthy
 *     max (~4.5 min) for a run that reached `running` but never terminated.
 * The truthful DB-side recovery (FAILED:api_unavailable from the run's own
 * router-timeout, and the stuck-run watchdog) still fires independently; this is
 * the last-resort client guard so the CEO is never stranded on a dead run.
 *
 * Network law: uses getDealScreenStatus from @/lib/api — no direct fetch.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getDealScreenStatus } from '@/lib/api';

const POLL_INTERVAL_MS = 4000;
const TERMINAL_STATES = ['complete', 'failed', 'partial'];

// Escape-hatch deadlines (see header). Generous on purpose — a healthy run must
// never be shown as failed while it is genuinely still running.
const NEVER_STARTED_DEADLINE_MS = 150000; // 2.5 min stuck at not_started/no-status → dead
const HARD_CEILING_MS = 420000;           // 7 min absolute ceiling for any non-terminal run

// error_key on a client-synthesised failure. The component maps it to business
// copy; this fallback message is plain-English and never shows raw codes.
export const FRONTEND_TIMEOUT_ERROR_KEY = 'frontend_timeout';
const FRONTEND_TIMEOUT_MESSAGE =
  "The analysis couldn't be completed — it didn't respond in time. Please try again.";

/**
 * @param {string|null} startupId — startup to poll
 * @param {number} runNonce — bump this (even with the same startupId) to
 *   re-arm polling after a Re-run. When nonce changes, the effect re-fires:
 *   pollStartRef and sawRunningRef reset, status resets to null, and a fresh
 *   poll window begins. This is what clears the perpetual "Re-running…" overlay
 *   after a Re-run completes (WI-DS-PROGRESS-VISIBILITY-BUILD-001).
 *   Default 0 — existing callers that do not pass it are unaffected.
 */
export default function useDealScreenStatus(startupId, runNonce = 0) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const pollStartRef = useRef(0);
  const sawRunningRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const synthesizeFailure = useCallback(() => {
    // Mark a dead run terminal so the UI unlocks + shows error + Retry.
    setStatus({
      state: 'failed',
      error_key: FRONTEND_TIMEOUT_ERROR_KEY,
      error_message: FRONTEND_TIMEOUT_MESSAGE,
      current_phase: null,
      current_phase_label: null,
      progress_detail: null,
      phases_completed: [],
    });
    stopPolling();
  }, [stopPolling]);

  const fetchStatus = useCallback(async () => {
    if (!startupId) return;
    let observedState = null;
    try {
      const data = await getDealScreenStatus(startupId);
      observedState = data?.state ?? null;
      setStatus(data);
      setError(null);
      if (observedState === 'running') sawRunningRef.current = true;
      if (TERMINAL_STATES.includes(observedState)) {
        stopPolling();
        return;
      }
    } catch (err) {
      // 404 / network error: treat as "no status yet" for the deadline timers —
      // a dropped run whose doc never got a status (or never existed) lands here.
      setError(err);
    } finally {
      setLoading(false);
    }

    // Escape-hatch evaluation (only reached for non-terminal / errored polls).
    const elapsed = Date.now() - pollStartRef.current;
    if (elapsed >= HARD_CEILING_MS) {
      synthesizeFailure();
      return;
    }
    if (!sawRunningRef.current && elapsed >= NEVER_STARTED_DEADLINE_MS) {
      // Never advanced past not_started/no-status within the grace window → dead.
      synthesizeFailure();
    }
  }, [startupId, stopPolling, synthesizeFailure]);

  useEffect(() => {
    if (!startupId) {
      setStatus(null);
      return;
    }
    // Reset on every (startupId, runNonce) change — this is the re-arm path.
    // When nonce bumps (Re-run clicked), pollStartRef and sawRunningRef reset
    // so the escape-hatch deadlines start fresh for the new run.
    setLoading(true);
    setStatus(null);
    pollStartRef.current = Date.now();
    sawRunningRef.current = false;
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return stopPolling;
    // runNonce is intentionally in the deps: bumping it re-arms polling even when
    // startupId is unchanged (the Re-run case). fetchStatus/stopPolling are stable
    // useCallbacks; startupId is captured by fetchStatus's closure.
  }, [startupId, runNonce, fetchStatus, stopPolling]);

  return { status, loading, error, isTerminal: TERMINAL_STATES.includes(status?.state) };
}
